(() => {
  const eventNames = {
    view: 'subscription_option_view',
    selected: 'subscription_option_selected',
    addToCart: 'subscription_add_to_cart',
    checkoutStarted: 'subscription_checkout_started',
  };
  const cartFlagKey = 'bcw_subscription_in_cart';
  const pendingEvents = [];
  const viewedOptions = new Set();
  const observedBlocks = new WeakSet();
  let retryTimer;
  let checkoutEventAt = 0;
  let subscriptionScanScheduled = false;

  function getProductHandle() {
    const match = window.location.pathname.match(/\/products\/([^/?#]+)/);
    return match ? match[1] : '';
  }

  function getContext(input, fallback = {}) {
    const block = input?.closest('.shopify_subscriptions_app_block');
    return {
      product: getProductHandle(),
      variant: input?.dataset.variantId || block?.dataset.variantId || fallback.variant || '',
      plan: input?.dataset.sellingPlanId || fallback.plan || '',
    };
  }

  function flushClarityEvents() {
    if (typeof window.clarity !== 'function') return;
    while (pendingEvents.length) {
      const item = pendingEvents.shift();
      Object.entries(item.tags).forEach(([key, value]) => {
        if (value) window.clarity('set', key, String(value));
      });
      window.clarity('event', item.name);
    }
    if (retryTimer) {
      window.clearInterval(retryTimer);
      retryTimer = undefined;
    }
  }

  function scheduleClarityFlush() {
    if (retryTimer) return;
    let attempts = 0;
    retryTimer = window.setInterval(() => {
      attempts += 1;
      flushClarityEvents();
      if (!pendingEvents.length || attempts >= 120) {
        window.clearInterval(retryTimer);
        retryTimer = undefined;
      }
    }, 1000);
  }

  function sendClarityEvent(name, context = {}) {
    pendingEvents.push({
      name,
      tags: {
        subscription_product: context.product,
        subscription_variant: context.variant,
        subscription_plan: context.plan,
      },
    });
    flushClarityEvents();
    if (pendingEvents.length) scheduleClarityFlush();
  }

  function setCartFlag(value) {
    try {
      window.sessionStorage.setItem(cartFlagKey, value ? '1' : '0');
    } catch (error) {
      return;
    }
  }

  function getCartFlag() {
    try {
      return window.sessionStorage.getItem(cartFlagKey) === '1';
    } catch (error) {
      return false;
    }
  }

  function cartDomHasSubscription() {
    if (document.querySelector('.bcw-drawer__item-selling-plan')) return true;
    return Array.from(document.querySelectorAll('.cart-item .product-option')).some((element) => {
      return /deliver every|subscription|subscribe/i.test(element.textContent || '');
    });
  }

  function cartHasSubscription() {
    if (document.querySelector('cart-drawer, cart-items, .cart__items')) {
      return cartDomHasSubscription();
    }
    return getCartFlag();
  }

  async function syncCartFlag() {
    try {
      const response = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
      if (!response.ok) return;
      const cart = await response.json();
      const hasSubscription = Array.isArray(cart.items)
        && cart.items.some((item) => Boolean(item.selling_plan_allocation));
      setCartFlag(hasSubscription);
    } catch (error) {
      return;
    }
  }

  function getSellingPlanInputs() {
    return Array.from(
      document.querySelectorAll(
        '.shopify_subscriptions_app_container input[data-radio-type="selling_plan"]'
      )
    );
  }

  const optionObserver = 'IntersectionObserver' in window
    ? new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting || entry.intersectionRatio < 0.25) return;
            const input = entry.target.querySelector('input[data-radio-type="selling_plan"]');
            if (!input) return;
            const context = getContext(input);
            const key = [context.product, context.variant, context.plan].join(':');
            if (viewedOptions.has(key)) return;
            viewedOptions.add(key);
            optionObserver.unobserve(entry.target);
            sendClarityEvent(eventNames.view, context);
          });
        },
        { threshold: 0.25 }
      )
    : null;

  function observeSubscriptionOptions() {
    getSellingPlanInputs().forEach((input) => {
      const block = input.closest('.shopify_subscriptions_app_block') || input.closest('fieldset');
      if (!block || observedBlocks.has(block)) return;
      observedBlocks.add(block);
      if (optionObserver) {
        optionObserver.observe(block);
        return;
      }
      if (block.getClientRects().length) {
        const context = getContext(input);
        const key = [context.product, context.variant, context.plan].join(':');
        if (!viewedOptions.has(key)) {
          viewedOptions.add(key);
          sendClarityEvent(eventNames.view, context);
        }
      }
    });
  }

  function scheduleSubscriptionScan() {
    if (subscriptionScanScheduled) return;
    subscriptionScanScheduled = true;
    window.requestAnimationFrame(() => {
      subscriptionScanScheduled = false;
      observeSubscriptionOptions();
    });
  }

  function selectedSellingPlanInput(planId = '', variantId = '') {
    return getSellingPlanInputs().find((input) => {
      const planMatches = !planId || String(input.dataset.sellingPlanId) === String(planId);
      const variantMatches = !variantId || String(input.dataset.variantId) === String(variantId);
      return planMatches && variantMatches && (input.checked || Boolean(planId));
    });
  }

  function handleSubscriptionSelection(event) {
    const input = event.target;
    if (!(input instanceof HTMLInputElement)) return;
    if (!input.matches('input[data-radio-type="selling_plan"]') || !input.checked) return;
    sendClarityEvent(eventNames.selected, getContext(input));
  }

  function handleCartUpdate(event) {
    if (!event || event.source !== 'product-form') {
      window.setTimeout(syncCartFlag, 400);
      return;
    }
    const allocation = event.cartData?.selling_plan_allocation;
    const planId = allocation?.selling_plan?.id || allocation?.selling_plan_id || '';
    if (!planId) {
      window.setTimeout(syncCartFlag, 400);
      return;
    }
    const variantId = event.productVariantId || event.cartData?.variant_id || '';
    const input = selectedSellingPlanInput(planId, variantId);
    setCartFlag(true);
    sendClarityEvent(eventNames.addToCart, getContext(input, { plan: planId, variant: variantId }));
  }

  function subscribeToCartUpdates(attempt = 0) {
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, handleCartUpdate);
      return;
    }
    if (attempt < 20) {
      window.setTimeout(() => subscribeToCartUpdates(attempt + 1), 250);
    }
  }

  function trackCheckoutStarted() {
    const now = Date.now();
    if (now - checkoutEventAt < 1500 || !cartHasSubscription()) return;
    checkoutEventAt = now;
    sendClarityEvent(eventNames.checkoutStarted);
  }

  function isCheckoutTrigger(element) {
    return Boolean(
      element?.closest('button[name="checkout"], input[name="checkout"], a[href*="/checkout"]')
    );
  }

  function initialize() {
    observeSubscriptionOptions();
    subscribeToCartUpdates();
    syncCartFlag();
    new MutationObserver(scheduleSubscriptionScan).observe(document.documentElement, {
      childList: true,
      subtree: true,
    });
    document.addEventListener('change', handleSubscriptionSelection, true);
    document.addEventListener(
      'click',
      (event) => {
        if (isCheckoutTrigger(event.target)) trackCheckoutStarted();
      },
      true
    );
    document.addEventListener(
      'submit',
      (event) => {
        if (isCheckoutTrigger(event.submitter)) trackCheckoutStarted();
      },
      true
    );
    document.addEventListener('shopify:section:load', observeSubscriptionOptions);
    window.addEventListener('CookiebotOnAccept', flushClarityEvents);
    window.addEventListener('CookiebotOnConsentReady', flushClarityEvents);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialize, { once: true });
  } else {
    initialize();
  }
})();
